/**
 * Round DMS-IssueRound-1 (2026) — Issues 2 & 3.
 *
 * Shared, dependency-free helpers that turn clipboard payloads (what Excel,
 * Google Sheets, LibreOffice … put on the clipboard) into a plain cell
 * matrix. Used by:
 *   • the DMS rich-text editor (dmsQuillTable.js) — Excel range → table;
 *   • the ECN Product Information grid (EcnProductInfoTab.jsx) — Excel
 *     rows → product lines.
 *
 * Nothing here depends on Excel itself: any tab-separated text/plain or any
 * text/html that contains a <table> is understood.
 *
 * A "matrix" is:  { rows: [ { header: boolean, cells: string[] }, … ] }
 * Every row is padded to the widest row so columns never shift.
 */

const normaliseNewlines = (s) => String(s ?? "").replace(/\r\n?/g, "\n");

/** Collapse runs of whitespace (but not explicit line breaks) and trim. */
export function cleanCellText(value) {
    return String(value ?? "")
        .replace(/\u00a0/g, " ")
        .split("\n")
        .map((line) => line.replace(/[ \t\f\v]+/g, " ").trim())
        .join("\n")
        .replace(/^\n+|\n+$/g, "");
}

function padRows(rows) {
    const width = rows.reduce((w, r) => Math.max(w, r.cells.length), 0);
    rows.forEach((r) => {
        while (r.cells.length < width) r.cells.push("");
    });
    return rows;
}

/**
 * Tab-separated text (Excel's text/plain flavour) → matrix, or null when the
 * text does NOT look tabular. Rules:
 *   • lines split on \n (after \r\n normalisation); trailing empty lines
 *     (Excel always appends one) are ignored;
 *   • with the default options it is a table only when EVERY non-blank line
 *     contains a TAB — plain prose ("This is a paragraph.") and multi-line
 *     text without tabs → null → caller keeps default paste behaviour;
 *   • `allowSingleColumn: true` (Product Information grid) additionally
 *     accepts ≥2 lines WITHOUT tabs as a one-column range — that is exactly
 *     what Excel puts on the clipboard for a single copied column.
 * Note: a single cell copied from Excel arrives as "value\n" — no tab, one
 * line → null (stays plain text) in both modes.
 */
export function matrixFromTsv(text, { allowSingleColumn = false } = {}) {
    const norm = normaliseNewlines(text);
    let lines = norm.split("\n");
    // Drop trailing empty line(s) — Excel terminates the payload with one.
    while (lines.length && lines[lines.length - 1].trim() === "") lines.pop();
    if (lines.length === 0) return null;
    const nonBlank = lines.filter((l) => l.trim() !== "");
    if (nonBlank.length === 0) return null;

    const hasTabs = norm.includes("\t");
    if (hasTabs) {
        // Every non-blank line must be tab-separated to count as a range.
        if (!nonBlank.every((l) => l.includes("\t"))) return null;
    } else {
        // No tabs at all: only a multi-line single column, and only when asked.
        if (!allowSingleColumn || lines.length < 2) return null;
    }

    const rows = lines.map((line) => ({
        header: false,
        cells: line.split("\t").map(cleanCellText),
    }));
    return { rows: padRows(rows) };
}

/** Text content of an element with <br> and block boundaries as newlines. */
function textWithBreaks(node) {
    let out = "";
    const walk = (n) => {
        if (n.nodeType === 3) { out += n.nodeValue; return; }               // TEXT_NODE
        if (n.nodeType !== 1) return;                                        // ELEMENT_NODE only
        const tag = n.tagName;
        if (tag === "BR") { out += "\n"; return; }
        if (tag === "SCRIPT" || tag === "STYLE") return;                     // never read code
        const block = tag === "P" || tag === "DIV" || tag === "LI";
        if (block && out && !out.endsWith("\n")) out += "\n";
        n.childNodes.forEach(walk);
        if (block && !out.endsWith("\n")) out += "\n";
    };
    walk(node);
    return cleanCellText(out);
}

/**
 * Read a DOM <table> element into a matrix. Handles thead/tbody/tfoot,
 * <th> cells and Excel's nested formatting (font/span/b …). Only TEXT is
 * read — markup, attributes and scripts in the source are discarded, which
 * is what keeps pasted HTML safe.
 */
export function matrixFromTableElement(table) {
    if (!table || !table.rows) return null;
    const rows = [];
    Array.from(table.rows).forEach((tr) => {
        const cells = Array.from(tr.cells);
        if (cells.length === 0) return;
        const inHead = tr.parentElement && tr.parentElement.tagName === "THEAD";
        const allTh = cells.every((c) => c.tagName === "TH");
        rows.push({
            header: Boolean(inHead || allTh),
            cells: cells.map(textWithBreaks),
        });
    });
    if (rows.length === 0) return null;
    return { rows: padRows(rows) };
}

/**
 * text/html clipboard flavour → matrix from the FIRST <table>, or null when
 * the HTML has no table. Parsed with DOMParser into an inert document —
 * scripts do not run, no network fetches, nothing is inserted in the page.
 */
export function matrixFromHtml(html) {
    const src = String(html ?? "");
    if (!/<table[\s>]/i.test(src)) return null;
    if (typeof DOMParser === "undefined") return null;
    let doc;
    try {
        doc = new DOMParser().parseFromString(src, "text/html");
    } catch {
        return null;
    }
    const table = doc.querySelector("table");
    return table ? matrixFromTableElement(table) : null;
}

/** True when the matrix is exactly one cell — treated as plain text. */
export function isSingleCell(matrix) {
    return Boolean(matrix)
        && matrix.rows.length === 1
        && matrix.rows[0].cells.length === 1;
}

/** Number of non-empty cells (used to ignore "all blank" ranges). */
export function nonEmptyCellCount(matrix) {
    if (!matrix) return 0;
    return matrix.rows.reduce(
        (n, r) => n + r.cells.filter((c) => c.trim() !== "").length, 0);
}

/**
 * Convenience for paste handlers: pick the best clipboard flavour.
 * Prefers an HTML <table> (keeps Excel's own cell boundaries, which are
 * immune to tabs/newlines inside cells); falls back to tab-separated text.
 * Returns null when the clipboard does not hold tabular data.
 */
export function matrixFromClipboardData(clipboardData, options = {}) {
    if (!clipboardData) return null;
    let html = "";
    let text = "";
    try { html = clipboardData.getData("text/html") || ""; } catch { html = ""; }
    try { text = clipboardData.getData("text/plain") || ""; } catch { text = ""; }
    return matrixFromHtml(html) || matrixFromTsv(text, options);
}

export default matrixFromClipboardData;
